import { ExpandableText, Notification } from "@r360/library";
import React, { useState } from "react";
import { productTypeNames } from "../../Constants";
import useAppSelector from "../../hooks/useAppSelector";
import useCustomTranslate from "../../hooks/useCustomTranslate";
import useTranslate from "../../hooks/useTranslate";
import {
    AccommodationResultPage,
    ActivitiesPage,
    BikeRentalPage,
    LettingPage,
    PackageResultPage,
    SkipassPage,
} from "../../pages";
import { requestLoadingByResvTypeSelector } from "../../Selectors";
import { userSelector } from "../../selectors/Selectors";
import { TReservationType } from "../../store/types";
import PageContainer from "../PageContainer";
import { RichText } from "../UI";
import "./SearchReservationTypes.scss";

const ActiveReservationType = ({ activeReservationType }: { activeReservationType: TReservationType }) => {
    switch (activeReservationType.type) {
        case productTypeNames.SKIPASS: {
            return <SkipassPage reservationTypeid={activeReservationType.id} />;
        }
        case productTypeNames.ACTIVITY: {
            return <ActivitiesPage reservationTypeid={activeReservationType.id} />;
        }
        case productTypeNames.LETTING: {
            return <LettingPage reservationTypeid={activeReservationType.id} />;
        }
        case productTypeNames.RENTAL: {
            return <BikeRentalPage reservationTypeid={activeReservationType.id} />;
        }
        case productTypeNames.ACCOMMODATION: {
            return <AccommodationResultPage reservationTypeid={activeReservationType.id} />;
        }
        case productTypeNames.PACKAGE: {
            return <PackageResultPage reservationTypeid={activeReservationType.id} />;
        }
        default: {
            return <div>Innehåll för ${activeReservationType.description}</div>;
        }
    }
};

export const SearchReservationTypes = ({ activeReservationType }: { activeReservationType: TReservationType }) => {
    const t = useTranslate();
    const ct = useCustomTranslate();
    const isOwner = !!useAppSelector(userSelector)?.isOwner;
    const [closedNotifications, setClosedNotifications] = useState<number[]>([]);
    const fetchingReservationResult = useAppSelector(state =>
        requestLoadingByResvTypeSelector(state, "fetchReservationTypeResults", activeReservationType.id)
    );

    const reservationHeaderText = activeReservationType.useGeneralInfo
        ? ct(`reservation_type:${activeReservationType.id}:general_info`)
        : isOwner
        ? t("book.header.owner_information")
        : t("book.header.information");

    const handleCloseNotification = () => {
        // Use timeout to await close-animation of Notification.
        setTimeout(() => setClosedNotifications?.([...closedNotifications, activeReservationType.id]), 1000);
    };

    const showNotification =
        reservationHeaderText.length > 0 && !closedNotifications.includes(activeReservationType.id);

    return (
        <div className="search-reservation-types">
            <PageContainer>
                <div className="search-reservation-types__notification">
                    <Notification
                        key={activeReservationType.id} // Re-render Notification when activeReservationType changes.
                        show={showNotification}
                        type="info"
                        onClose={handleCloseNotification}
                        className="u-mb-12"
                        disabled={fetchingReservationResult} // We disable the notification when fetching results to further indicate loading.
                    >
                        <ExpandableText
                            text={reservationHeaderText}
                            collapsedButtonText="Läs mer"
                            expandedButtonText="Läs mindre"
                            collapsedLineClamp={3}
                            renderText={text => <RichText content={text} />}
                        />
                    </Notification>
                </div>
                {activeReservationType ? <ActiveReservationType activeReservationType={activeReservationType} /> : null}
            </PageContainer>
        </div>
    );
};
